import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AdditionalDocumentsData,
  Application,
  BankingData,
  BankingState,
  CareerInformation,
  ExpensesIncome,
  HomeAddress,
  HouseholdInformation,
} from '@/global/models/IBankingSteps';
import { RootState } from '@/store/redux';

const SLICE_NAME = 'banking/steps';

const bankingData: BankingData = {
  personalData: {
    application: {
      salutation: '',
      title: '',
      firstName: '',
      lastName: '',
      birthName: '',
      birthPlace: '',
      birthDate: null,
      phoneNumber: '',
      idType: '',
      idNumberPass: '',
      issuingAuthority: '',
      issuingDate: null,
      expiryDate: null,
      emailAddress: '',
    },
    homeAddress: {
      currentResidence: {
        zipCode: 0,
        city: '',
        street: '',
        streetNumber: '',
      },
      country: '',
      residenceDate: null,
      previousResidence: {
        zipCode: 0,
        city: '',
        street: '',
        streetNumber: '',
      },
    },
  },
  householdJob: {
    householdInformation: {
      maritalStatus: '',
      numberOfChildren: 0,
      livingSituation: '',
      peopleInHousehold: 0,
      homeOwnership: false,
      nationality: '',
      residencePermit: '',
      residencePermitUntilDate: null,
    },
    careerInformation: {
      professionalGroup: '',
      jobTitle: '',
      employmentType: '',
      employedDate: null,
      employedUntilDate: null,
      employerName: '',
      hasPreviousEmployer: false,
      jobPlace: {
        zipCode: 0,
        city: '',
        street: '',
        streetNumber: '',
      },
      previousEmployerName: '',
      previousJobPlace: {
        zipCode: 0,
        city: '',
        street: '',
        streetNumber: '',
      },
    },
  },
  expensesIncome: {
    netIncome: 0,
    otherIncome: 0,
    pension: 0,
    rentalIncome: 0,
    maintenanceIncome: 0,
    deduction: 0,
    otherLoans: 0,
    maintenanceObligations: 0,
    mortgages: 0,
    otherInformation: 0,
    privateHealthInsurance: '',
    iban: '',
    bankName: '',
    installment: '',
  },
  additionalDocuments: {
    files: [],
    actingFlag: false,
    contractFlag: true, // todo: remove after GW fix / newsletter schema fix
    privacyFlag: true, // todo: remove after GW fix / newsletter schema fix
  },
};

export const initialState: BankingState = {
  data: bankingData,
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setPersonalDataStep(
      state,
      action: PayloadAction<{
        application: Application;
        homeAddress: HomeAddress;
      }>
    ) {
      state.data.personalData = {
        application: action.payload.application,
        homeAddress: action.payload.homeAddress,
      };
    },

    setHouseholdJobStep(
      state,
      action: PayloadAction<{
        householdInformation: HouseholdInformation;
        careerInformation: CareerInformation;
      }>
    ) {
      state.data.householdJob = {
        householdInformation: action.payload.householdInformation,
        careerInformation: action.payload.careerInformation,
      };
    },

    setExpensesIncome(
      state,
      action: PayloadAction<{
        expensesIncome: ExpensesIncome;
      }>
    ) {
      state.data.expensesIncome = { ...action.payload.expensesIncome };
    },

    setAdditionalDocuments(
      state,
      action: PayloadAction<{
        additionalDocuments: AdditionalDocumentsData;
      }>
    ) {
      state.data.additionalDocuments = {
        ...action.payload.additionalDocuments,
      };
    },

    reset() {
      return initialState;
    },
  },
});

export const {
  setPersonalDataStep,
  setHouseholdJobStep,
  setExpensesIncome,
  setAdditionalDocuments,
  reset,
} = slice.actions;

export const getBankingRequestState = (state: RootState): BankingData =>
  state.pages.bankingRequest.page.data;

export default slice.reducer;
